import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { CURRENT_CLIENT } from '@/constants'
import { isPublicSharePath } from '@/constants/public-share-routes'
import {
  resolveDocumentFaviconSrc,
  type ClientMarkConfig,
} from '@/lib/client-mark-src'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const SEGMENT_LABELS: Record<string, string> = {
  candidate: 'Candidate',
  employer: 'Employer',
  recruiter: 'Recruiter',
  interviewer: 'Interviewer',
  platform: 'Platform',
  developer: 'Developer',
  calls: 'Calls',
  patient: 'Patient',
  practitioner: 'Practitioner',
  onboarding: 'Onboarding',
  settings: 'Settings',
  login: 'Sign in',
  signup: 'Sign up',
  register: 'Sign up',
  profile: 'Profile',
  files: 'Files',
  memories: 'Memories',
  threads: 'Threads',
  notifications: 'Notifications',
  feedback: 'Feedback',
  concepts: 'Concepts',
  topology: 'Topology',
}

function looksLikeId(segment: string): boolean {
  if (!segment) return true
  if (UUID_RE.test(segment)) return true
  if (/^\d+$/.test(segment)) return true
  const digits = (segment.match(/\d/g) ?? []).length
  return digits >= 4 && digits / segment.length > 0.3
}

function humanizeSegment(segment: string): string {
  const words = segment.split('-').filter(Boolean)
  if (words.length === 0) return ''
  const [first, ...rest] = words
  return [
    first.charAt(0).toUpperCase() + first.slice(1).toLowerCase(),
    ...rest.map((word) => word.toLowerCase()),
  ].join(' ')
}

export function formatDocumentTitle(pathname: string, siteTitle: string): string {
  if (pathname === '/' || pathname === '') return siteTitle

  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return siteTitle

  let idx = segments.length - 1
  while (idx >= 0 && looksLikeId(segments[idx])) {
    idx -= 1
  }
  if (idx < 0) return siteTitle

  const segment = segments[idx]
  const label = SEGMENT_LABELS[segment] ?? humanizeSegment(segment)
  return `${label} · ${siteTitle}`
}

export function useDocumentHead() {
  const location = useLocation()
  const siteTitle =
    (CURRENT_CLIENT as { siteTitle?: string }).siteTitle || CURRENT_CLIENT.name

  useEffect(() => {
    if (isPublicSharePath(location.pathname)) return
    document.title = formatDocumentTitle(location.pathname, siteTitle)
  }, [location.pathname, siteTitle])

  useEffect(() => {
    const cfg = CURRENT_CLIENT as ClientMarkConfig & { siteTitle?: string; name?: string }
    const faviconHref = resolveDocumentFaviconSrc({
      faviconPath: cfg.faviconPath,
      faviconStyle: cfg.faviconStyle,
      brandIcon: cfg.brandIcon,
      theme: cfg.theme,
    })
    const faviconType = faviconHref.startsWith('data:')
      ? 'image/svg+xml'
      : faviconHref.endsWith('.svg')
        ? 'image/svg+xml'
        : 'image/png'

    let faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement
    if (!faviconLink) {
      faviconLink = document.createElement('link')
      faviconLink.rel = 'icon'
      document.head.appendChild(faviconLink)
    }

    faviconLink.href = faviconHref
    faviconLink.type = faviconType
  }, [])
}
